import { createMutation, useQueryClient } from '@tanstack/svelte-query';
import type {
  StreakResponse,
  DailyPuzzle,
  DateCell,
  GameType,
  LeaderboardEntry,
  SLeaderboardEntry,
  TwistPuzzle,
  Puzzle,
} from '../types';
import { parseDate } from '~/utils/dates';
import {
  getCalendarQueryKey,
  getClassicRankKey,
  getStreakQueryKey,
  stringifyPuzzleKey,
  getLatestPuzzleKey,
  classicSnapshotQueryKey,
} from './queries.svelte';
import { saveDailyGame, saveTwistGame, saveClassicGame } from './analytics.remote.js';
import {
  upsertClassicSnapshot,
  getClassicSnapshot,
  getClassicSnapshotById,
  deleteClassicSnapshotById,
} from './snapshots.remote.js';
import type { ClassicSnapshotMeta } from './snapshots.remote.js';
import { wordToString } from '~/board/utils';
import { ClassicGame } from '~/store/classic.svelte';
import type { TwistGame } from '~/store/twist.svelte';
import type { DailyGame } from '~/store/daily.svelte'
import { syncModal } from '~/routes/classic/SyncModal.svelte';
import { PersistedState } from 'runed';
import { playerName } from '~/auth/utils.svelte';
import * as Sentry from '@sentry/svelte';
import { freshQuery } from '~/utils/freshQuery';

export const saveDailyAnalytics = () => {
  const queryClient = useQueryClient();
  return createMutation(() => ({
    mutationFn: async (game: DailyGame) => {
      const record = {
        id: game.id,
        key: game.key,
        date: game.date,
        mode: game.variant,
        created_at: new Date().toISOString(),
        words: await game.getAll(),
        turns: await game.getTurns(),
        duration_seconds: game.duration,
        stars: game.stars,
        full_clear: !!game.fullClear,
        final_board: game.finalBoard?.map((row) =>
          row.map((tile) => tile.letter),
        ),
      };
      const streak = await saveDailyGame(record);
      return {
        game,
        streak,
      };
    },
    onSuccess: async ({ game, streak }) => {
      const words = await game.getAll();
      const date = parseDate(game.date);
      const key = ['daily', stringifyPuzzleKey(game)];
      const latestKey = ['daily', getLatestPuzzleKey(game.variant)];
      const prev = queryClient.getQueryData<DailyPuzzle>(key);
      const latest = queryClient.getQueryData<DailyPuzzle>(latestKey);
      const calendarKey =
        date &&
        getCalendarQueryKey(
          game.variant,
          date.getUTCMonth(),
          date.getUTCFullYear(),
        );

      if (streak?.total_games) {
        queryClient.setQueryData<StreakResponse>(
          getStreakQueryKey(game.variant),
          streak,
        );
      }
      if (game.id === latest?.id) {
        queryClient.setQueryData<DailyPuzzle>(latestKey, {
          ...$state.snapshot(game),
          words: words,
          completed: true,
          board: prev!.board,
          turns: game.turns, //await game.getTurns(), TODO
          stars: game.stars,
          fullClear: Boolean(game.fullClear),
        });
      }

      // replace old stats;
      if (
        !prev?.stars ||
        game.stars > prev.stars ||
        (game.stars === prev.stars && game.fullClear) ||
        // TODO turns.length vs numTurms
        (game.stars === prev.stars &&
          game.fullClear === prev.fullClear &&
          game.turns < prev.turns)
      ) {
        // update puzzle summary
        // we don't want to reset the puzzle (i.e. hide the game over modal)
        queryClient.setQueryData<DailyPuzzle>(key, (prev) => {
          if (!prev) {
            console.error('no prev', game);
          }
          // TODO this is causing board to reset
          return {
            ...$state.snapshot(game),
            completed: true,
            board: prev!.board,
            stars: game.stars,
            words: words,
            // TODO filter out shuffle turns
            turns: game.turns,
            fullClear: Boolean(game.fullClear),
          };
        });
        // update calendar
        if (calendarKey) {
          queryClient.setQueryData<Record<string, DateCell>>(
            calendarKey,
            (prev) => {
              if (!prev) return prev;
              return {
                ...prev,
                [game.date]: {
                  id: game.id,
                  date: game.date,
                  stars: game.stars,
                  fullClear: !!game.fullClear,
                  disabled: false,
                  empty: false,
                },
              };
            },
          );
        }
      }
    },
  }));
};

export const saveTwistAnalytics = () => {
  const queryClient = useQueryClient();
  return createMutation(() => ({
    mutationFn: async (game: TwistGame) => {
      const record = {
        id: game.id,
        key: game.key,
        date: game.date,
        mode: game.variant,
        turns: game.turns,
        created_at: new Date().toISOString(),
        // duration_seconds: game.durationSeconds,
        duration_seconds: Math.round((Date.now() - game.startedAt!) / 1000),
      };
      const streak = await saveTwistGame(record as any);
      return {
        game,
        streak,
      };
    },
    onSuccess: async ({ game, streak }) => {
      const date = parseDate(game.date);
      const key = ['twist', stringifyPuzzleKey(game)];
      const latestKey = ['twist', getLatestPuzzleKey(game.variant)];
      const prev = queryClient.getQueryData<TwistPuzzle>(key);
      const latest = queryClient.getQueryData<TwistPuzzle>(latestKey);
      const calendarKey =
        date &&
        getCalendarQueryKey(
          game.variant,
          date.getUTCMonth(),
          date.getUTCFullYear(),
        );

      if (streak?.total_games) {
        queryClient.setQueryData<StreakResponse>(
          getStreakQueryKey(game.variant),
          streak,
        );
      }
      if (game.id === latest?.id) {
        const r = queryClient.setQueryData<TwistPuzzle>(latestKey, {
          ...game,
          completed: true,
          board: prev!.board,
          turns: game.turns!.filter((t) => t.coords.length === 2),
        });
      }
      // replace old stats;
      if (!prev || (prev && game.durationSeconds! > prev.durationSeconds)) {
        // update puzzle summary
        // we don't want to reset the puzzle (i.e. hide the game over modal)
        queryClient.setQueryData<TwistPuzzle>(key, (prev) => {
          if (!prev) {
            console.error('no prev', game);
          }
          // TODO this is causing board to reset
          return {
            ...game,
            completed: true,
            board: prev!.board,
            // filter out shuffle turns
            turns: game.turns!.filter((t) => t.coords.length === 2),
          };
        });
        // update calendar
        // if (calendarKey) {
        //   queryClient.setQueryData<Record<string, DateCell>>(calendarKey, (prev) => {
        //     if (!prev) {
        //       prev = {}
        //     }
        //     prev[game.date] = {
        //       id: game.id,
        //       date: game.date,
        //       disabled: false,
        //       empty: false,
        //     }
        //     return prev;
        //   });
        // }
      }
    },
  }));
};

export const saveClassicAnalytics = () => {
  const queryClient = useQueryClient();
  return createMutation(() => ({
    mutationFn: async (game: ClassicGame) => {
      const createdAt = new Date();
      const words = await game.getAll();
      const best = await game.getPage('score', 0);
      const record = {
        created_at: createdAt.toISOString(),
        id: game.id,
        turns: game.turns,
        user_name: playerName.current,
        score: game.score,
        words: words.map(wordToString),
        duration_seconds: game.duration,
        final_board: $state.snapshot(game.board),
        best_word: best[0],
      };
      return await saveClassicGame(record as any);
    },
    onSuccess: (data: number) => {
      queryClient.setQueryData(getClassicRankKey('week'), data);
    },
  }));
};

type ReportMutationProps = {
  word: string;
  reason: string;
};

export const reportWordMutation = () =>
  createMutation(() => ({
    mutationKey: ['definition', 'report'],
    mutationFn: async (props: ReportMutationProps) => {
      await fetch('/api/report', {
        method: 'POST',
        body: JSON.stringify({
          word: props.word,
          reason: props.reason,
        }),
      });
    },
  }));

export const sendFeedbackMutation = (type: GameType) =>
  createMutation(() => ({
    mutationKey: ['feedback', type],
    mutationFn: async (feedback: FormData) => {
      await fetch('/api/feedback', {
        method: 'POST',
        body: feedback,
      });
    },
  }));

export const subscribeToMailingListMutation = () =>
  createMutation(() => ({
    mutationKey: ['subscribe', 'mailing'],
    mutationFn: async (body: FormData) => {
      await fetch('/api/mailing-list', {
        method: 'POST',
        body,
      });
    },
  }));

let cloudWordPointer = new PersistedState('classic-word-pointer', 0);
let cloudTurnPointer = new PersistedState('classic-turn-pointer', 0);
let pointersInitialized = false;

export const saveClassicSnapshot = (
  lastSyncedAt: { current: number | null },
) => {
  const queryClient = useQueryClient();

  return createMutation(() => ({
    mutationFn: async ({ game, overwrite = false }: { game: ClassicGame; overwrite?: boolean }) => {
      if (game.turns === 0) return null;

      if (!pointersInitialized) {
        try {
          const snap = await queryClient.ensureQueryData<ClassicSnapshotMeta>({
            queryKey: classicSnapshotQueryKey(),
            queryFn: () => freshQuery(getClassicSnapshot()),
          });
          if (snap?.id === game.id) {
            cloudWordPointer.current = snap.words;
            cloudTurnPointer.current = snap.turns;
          }
        } catch {
          // not logged in, not premium, or no snapshot — pointers stay at 0
        }
        pointersInitialized = true;
      }

      const gameState = game.serialize();

      // if idle saving with no changes
      if (
        cloudWordPointer.current === gameState.words
        && cloudTurnPointer.current === gameState.turns
      ) return;

      const [newWords, newTurns] = await Promise.all([
        game.getAll('index', cloudWordPointer.current),
        game.getTurns(cloudTurnPointer.current),
      ]);

      const payload = {
        ...gameState,
        editedAt: new Date().toISOString(),
        lastSyncedAt: lastSyncedAt.current !== null ? new Date(lastSyncedAt.current).toISOString() : undefined,
        overwrite,
        wordPointer: cloudWordPointer.current,
        turnPointer: cloudTurnPointer.current,
        newWords,
        newTurns,
      };

      const result = await upsertClassicSnapshot(payload);
      if (!result.ok) {
        Sentry.captureException({
          result
        })
        throw new Error(result.reason);
      }

      return {
        ...gameState,
        syncedAt: result.editedAt,
        words: cloudWordPointer.current + newWords.length,
        turns: cloudTurnPointer.current + newTurns.length
      }
    },
    onSuccess: (payload) => {
      if (!payload) return;
      lastSyncedAt.current = new Date(payload.syncedAt).getTime();
      cloudWordPointer.current = payload.words;
      cloudTurnPointer.current = payload.turns;
      queryClient.setQueryData<ClassicSnapshotMeta>(classicSnapshotQueryKey(), {
        id: payload.id,
        score: payload.score,
        swaps: payload.swaps,
        latest_word: $state.snapshot(payload.latestWord) ?? null,
        edited_at: payload.syncedAt,
        completed: false,
        words: payload.words,
        turns: payload.turns,
      });
    },
    onError: (err: unknown, variables) => {
      const message = (err as any)?.body?.message ?? (err instanceof Error ? err.message : '');
      let gameState: unknown;
      try {
        gameState = variables?.game?.serialize();
      } catch (serializeErr) {
        gameState = { serializeError: String(serializeErr) };
      }
      const snapshotState = queryClient.getQueryData<ClassicSnapshotMeta>(
        classicSnapshotQueryKey(),
      );
      Sentry.captureException(err, {
        tags: { mutation: 'saveClassicSnapshot' },
        extra: {
          reason: message || 'unknown',
          overwrite: variables?.overwrite ?? false,
          lastSyncedAt: lastSyncedAt.current,
          wordPointer: cloudWordPointer.current,
          turnPointer: cloudTurnPointer.current,
          gameState,
          snapshotState,
        },
      });

      if (message === 'pointer_mismatch') {
        pointersInitialized = false;
        syncModal.push();
      } else if (message === 'sync_conflict') {
        queryClient.invalidateQueries({ queryKey: classicSnapshotQueryKey() });
        syncModal.push();
      }
    },
  }));
};

export const deleteClassicSnapshot = () => {
  const queryClient = useQueryClient();
  return createMutation(() => ({
    mutationFn: async (id: string) => {
      await deleteClassicSnapshotById(id);
    },
    onSuccess: (_data, id) => {
      queryClient.setQueryData<import('./queries.svelte').ClassicSnapshotMeta[]>(
        ['games', 'classic', 'snapshots'],
        (prev) => prev?.filter((s) => s.id !== id) ?? [],
      );
    },
  }));
};

export const restoreClassicSnapshot = () =>
  createMutation(() => ({
    mutationFn: async (id: string): Promise<ClassicGame> => {
      const snapshot = await freshQuery(getClassicSnapshotById(id));
      cloudWordPointer.current = snapshot.words.length;
      cloudTurnPointer.current = snapshot.turns.length;

      const game = await ClassicGame.load(snapshot as unknown as Puzzle);
      // if game got saved before collapsing 
      await game.collapseBoard();
      game.disabled = false;
      game.latestWord = snapshot.words.at(-1);
      game.save();
      return game;
    },
  }));

export const getEntry = async (
  game: ClassicGame,
  playerName: string,
): Promise<SLeaderboardEntry & LeaderboardEntry> => ({
  name: playerName,
  userName: playerName,
  id: game.id,
  gameId: game.id,
  userId: '',
  score: game.score,
  bestStreak: 0,
  // uggh
  turns: game.turns,
  numTurns: game.turns,
  bestWord: (await game.getPage('score', 0))[0]?.word ?? [],
  numWords: game.words,
  date: new Date().toISOString(),
  // TODO
  rank: -1,
  day_rank: -1,
  week_rank: -1,
  all_rank: -1,
  local_rank: -1,
});
